// packages/api/src/hooks/useLocations.ts

import { useQuery } from '@tanstack/react-query';

import type { State, City } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

// Locations data is essentially immutable — cache aggressively.
const STALE_STATES = 24 * 60 * 60 * 1000; // 24h
const STALE_CITIES = 7 * 24 * 60 * 60 * 1000; // 7d
// Must fit in int32 for setTimeout (ceiling ~24d 20h). 20d is a comfortable bound.
const GC = 20 * 24 * 60 * 60 * 1000; // 20d

const IBGE_CITY_REGEX = /^\d{7}$/;

interface ApiListResponse<T> {
  data: T[];
}

interface ApiSingleResponse<T> {
  data: T;
}

/**
 * Fetch the 27 Brazilian states ordered by name.
 * Heavily cached (data is effectively static).
 */
export function useStates() {
  return useQuery({
    queryKey: queryKeys.locations.states(),
    queryFn: async () => {
      const res = await apiClient.get<ApiListResponse<State>>('/locations/states');
      return res.data.data;
    },
    staleTime: STALE_STATES,
    gcTime: GC,
  });
}

/**
 * Fetch municipalities of a given UF, optionally filtered by normalized name.
 * `enabled` checks keep the hook safe to call with undefined/invalid UF.
 */
export function useCitiesByState(uf: string | undefined, q?: string, limit?: number) {
  const normalizedUf = uf?.toUpperCase() ?? '';
  return useQuery({
    queryKey: queryKeys.locations.citiesByUf(normalizedUf, q),
    queryFn: async () => {
      const res = await apiClient.get<ApiListResponse<City>>(
        `/locations/states/${normalizedUf}/cities`,
        { params: { q: q || undefined, limit } },
      );
      return res.data.data;
    },
    enabled: /^[A-Z]{2}$/.test(normalizedUf),
    staleTime: STALE_CITIES,
    gcTime: GC,
  });
}

/**
 * Look up a city by its 7-digit IBGE code.
 * Useful when a CEP cascade yielded an ibgeCode but not a full city row.
 */
export function useCityByIbge(code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.locations.cityByIbge(code ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiSingleResponse<City>>(`/locations/cities/by-ibge/${code}`);
      return res.data.data;
    },
    enabled: !!code && IBGE_CITY_REGEX.test(code),
    staleTime: STALE_CITIES,
    gcTime: GC,
  });
}

/**
 * Fuzzy search cities by name (pg_trgm similarity on the backend).
 * Minimum 2 chars; debounce at the callsite.
 */
export function useSearchCities(q: string, limit?: number) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: queryKeys.locations.search(trimmed),
    queryFn: async () => {
      const res = await apiClient.get<ApiListResponse<City>>('/locations/cities/search', {
        params: { q: trimmed, limit },
      });
      return res.data.data;
    },
    enabled: trimmed.length >= 2,
    // Search results shouldn't be cached as aggressively — users iterate queries
    staleTime: 60 * 1000,
  });
}
